/**
 * The one list every library view is.
 *
 * Labs, scenarios, configuration items and devices are four different things, and an admin looking
 * for one of them is doing the same task every time: narrow by a word, narrow by a facet, see how
 * far a row reaches, then act on it. Four tables that each solved that slightly differently is
 * four sets of habits to learn, so this is the table and the views only supply what a row *is*.
 *
 * Fixed here, not negotiable per view: the tick column, the status dot beside the name, the "Used
 * in" count, when it was last touched, and the row menu. The facet columns in between are the
 * caller's, because what usefully distinguishes one item from another is not the same question for
 * a device as for a scenario.
 */

import React, { useMemo, useState } from 'react';
import { HbrCheckbox } from '@harbor/elements-react-standalone';
import { plus } from '@harbor/elements/icons';
import StbButton from '../security-toolbox-ui/StbButton';
import { LabOption } from '../../support/labService';
import { LabCard, LabDate, LabPanelState } from './LabShared';
import {
  LabAuthoringStatus,
  LabBulkBar,
  LabFacetFilter,
  LabRowAction,
  LabRowMenu,
  LabSearchBox,
  LabStatusDot,
  LabStatusLegend,
  LabUsage,
  LabUsageBadge,
} from './LabAdminShared';

/** A column the view supplies: the facets that distinguish its own rows. */
export type LabLibraryColumn<T> = {
  key: string;
  header: string;
  render: (row: T) => React.ReactNode;
  className?: string;
};

/**
 * One narrowing over a facet the rows already carry.
 *
 * `matches` rather than a field name because some of these are not fields: "reaches RTP" is a
 * question about a lab's publications, and "is attached anywhere" is a question about a count.
 */
export type LabLibraryFacet<T> = {
  key: string;
  label: string;
  options: LabOption<string>[];
  matches: (row: T, value: string) => boolean;
};

export type LabLibraryListProps<T extends { id: number }> = {
  title: string;
  subtitle?: React.ReactNode;
  rows: T[];
  loading?: boolean;
  error?: unknown;
  emptyMessage?: string;

  /** The name cell. Usually the name over the slug or key, since both get searched for. */
  nameOf: (row: T) => React.ReactNode;
  /** Free-text match. Owned by the view because which fields count differs per table. */
  search: (row: T, term: string) => boolean;
  facets?: LabLibraryFacet<T>[];
  columns?: LabLibraryColumn<T>[];

  statusOf?: (row: T) => { status: LabAuthoringStatus; title?: string };
  /** Everywhere this row is used. The reason an admin can edit a shared row with any confidence. */
  usagesOf?: (row: T) => LabUsage[];
  usageSubjectOf?: (row: T) => string;
  usageEmptyLabel?: string;
  /**
   * Header for the usage column. "Used in" is right for the three tiers that have parents and
   * wrong for a lab, which has none - a lab's blast radius is where it is delivered, not what
   * contains it, and a column headed "Used in" over a list of data centres reads as a lie.
   */
  usageHeader?: string;
  updatedOf?: (row: T) => string | null | undefined;

  rowActions?: (row: T) => LabRowAction[];
  /** Given the ticked rows. Returning none hides the bar even when rows are ticked. */
  bulkActions?: (selected: T[]) => LabRowAction[];

  newLabel?: string;
  onNew?: () => void;
  /** Extra controls beside the search box. For a view that needs one the others do not. */
  toolbarExtra?: React.ReactNode;

  /** Clicking anywhere on the row. The builder uses it to open a lab; the others may not. */
  onRowClick?: (row: T) => void;
  selectedId?: number | null;

  /**
   * Lifts the search term out to the caller, for the shell's global search box.
   *
   * Both or neither. With `onSearchTerm` given the box is controlled and the shell owns the term,
   * which is what lets one box in the top bar narrow whichever list is showing and count the
   * matches in the ones that are not. Without it the list keeps its own term, which is what the
   * builder's embedded pickers want.
   */
  searchTerm?: string;
  onSearchTerm?: (term: string) => void;
};

/**
 * Ticking rows.
 *
 * Held here rather than by the caller so a view cannot forget to clear it after a bulk delete -
 * a selection containing rows that no longer exist is how a second bulk action 404s halfway.
 * Keyed on ids and intersected with the rows on every render, so a row that has left the list
 * leaves the selection with it.
 */
const useSelection = <T extends { id: number }>(rows: T[]) => {
  const [ids, setIds] = useState<number[]>([]);

  const selected = useMemo(() => rows.filter((row) => ids.includes(row.id)), [rows, ids]);

  const toggle = (id: number) =>
    setIds((current) =>
      current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id],
    );

  const toggleAll = () =>
    setIds((current) => (current.length === rows.length ? [] : rows.map((row) => row.id)));

  return { ids, selected, toggle, toggleAll, clear: () => setIds([]) };
};

function LabLibraryList<T extends { id: number }>({
  title,
  subtitle,
  rows,
  loading,
  error,
  emptyMessage = 'Nothing here yet.',
  nameOf,
  search,
  facets = [],
  columns = [],
  statusOf,
  usagesOf,
  usageSubjectOf,
  usageEmptyLabel,
  usageHeader = 'Used in',
  updatedOf,
  rowActions,
  bulkActions,
  newLabel = 'New',
  onNew,
  toolbarExtra,
  onRowClick,
  selectedId,
  searchTerm,
  onSearchTerm,
}: LabLibraryListProps<T>) {
  const [ownTerm, setOwnTerm] = useState('');
  const [facetValues, setFacetValues] = useState<Record<string, string>>({});

  const controlled = !!onSearchTerm;
  const term = controlled ? searchTerm || '' : ownTerm;
  const setTerm = controlled ? onSearchTerm! : setOwnTerm;

  const filtered = useMemo(() => {
    const needle = term.trim().toLowerCase();

    return rows.filter((row) => {
      if (needle && !search(row, needle)) return false;

      return facets.every((facet) => {
        const value = facetValues[facet.key];

        return !value || facet.matches(row, value);
      });
    });
  }, [rows, term, facetValues, facets, search]);

  const selection = useSelection(filtered);
  const bulk = bulkActions ? bulkActions(selection.selected) : [];

  const narrowed = filtered.length !== rows.length;

  return (
    <LabCard
      title={`${title} (${filtered.length}${narrowed ? ` of ${rows.length}` : ''})`}
      subtitle={subtitle}
      actions={
        onNew ? (
          <StbButton role="add" size="small" icon={plus} onClick={async () => onNew()}>
            {newLabel}
          </StbButton>
        ) : undefined
      }
    >
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <LabSearchBox
          value={term}
          onChange={setTerm}
          placeholder={`Search ${title.toLowerCase()}`}
          className="min-w-[220px]"
        />

        {facets.map((facet) => (
          <LabFacetFilter
            key={facet.key}
            label={facet.label}
            value={facetValues[facet.key] || ''}
            options={facet.options}
            onChange={(value) =>
              setFacetValues((current) => ({ ...current, [facet.key]: value }))
            }
          />
        ))}

        {toolbarExtra}
      </div>

      <LabBulkBar count={selection.selected.length} actions={bulk} onClear={selection.clear} />

      <LabPanelState
        loading={loading}
        error={error}
        empty={!filtered.length}
        emptyMessage={
          narrowed || term
            ? 'Nothing matches that. Clear the search or the filters above.'
            : emptyMessage
        }
        loadingLabel={`Loading ${title.toLowerCase()}...`}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                {bulkActions && (
                  <th className="w-8 px-2 py-2">
                    <HbrCheckbox
                      size="small"
                      aria={{ label: 'Select every row shown' }}
                      checked={!!filtered.length && selection.ids.length === filtered.length}
                      indeterminate={
                        !!selection.ids.length && selection.ids.length < filtered.length
                      }
                      onHbr-change={() => selection.toggleAll()}
                    />
                  </th>
                )}
                <th className="px-3 py-2 font-semibold">Name</th>
                {columns.map((column) => (
                  <th className={`px-3 py-2 font-semibold ${column.className || ''}`} key={column.key}>
                    {column.header}
                  </th>
                ))}
                {usagesOf && <th className="px-3 py-2 font-semibold">{usageHeader}</th>}
                {updatedOf && <th className="px-3 py-2 font-semibold">Updated</th>}
                {rowActions && <th className="w-10 px-2 py-2" />}
              </tr>
            </thead>

            <tbody>
              {filtered.map((row) => {
                const status = statusOf?.(row);
                const actions = rowActions?.(row) || [];

                return (
                  <tr
                    key={row.id}
                    className={`border-b border-gray-100 align-top ${
                      selectedId === row.id ? 'bg-blue-50' : ''
                    } ${onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  >
                    {bulkActions && (
                      <td className="px-2 py-2" onClick={(evt) => evt.stopPropagation()}>
                        <HbrCheckbox
                          size="small"
                          aria={{ label: 'Select this row' }}
                          checked={selection.ids.includes(row.id)}
                          onHbr-change={() => selection.toggle(row.id)}
                        />
                      </td>
                    )}

                    <td className="px-3 py-2">
                      <div className="flex items-start gap-2">
                        {status && <LabStatusDot status={status.status} title={status.title} className="mt-1.5" />}
                        <div>{nameOf(row)}</div>
                      </div>
                    </td>

                    {columns.map((column) => (
                      <td className={`px-3 py-2 ${column.className || ''}`} key={column.key}>
                        {column.render(row)}
                      </td>
                    ))}

                    {usagesOf && (
                      <td className="px-3 py-2" onClick={(evt) => evt.stopPropagation()}>
                        <LabUsageBadge
                          usages={usagesOf(row)}
                          subject={usageSubjectOf?.(row) || 'this row'}
                          emptyLabel={usageEmptyLabel}
                        />
                      </td>
                    )}

                    {updatedOf && (
                      <td className="px-3 py-2 text-xs text-gray-600">
                        <LabDate value={updatedOf(row)} />
                      </td>
                    )}

                    {rowActions && (
                      <td className="px-2 py-2 text-right">
                        <LabRowMenu actions={actions} />
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </LabPanelState>

      {statusOf && <LabStatusLegend className="mt-3" />}
    </LabCard>
  );
}

export default LabLibraryList;
